import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';

type Severity = 'success' | 'error' | 'warning' | 'info';

/** Une instance d'alerte affichée à l'écran. */
interface AlertItem {
    id: number;
    severity: Severity;
    title: string;
    text?: string;
    closing?: boolean;
}

/** Signature des fonctions d'alerte exposées par le hook. */
type AlertFn = (title: string, text?: string) => void;

interface AlertContextValue {
    success: AlertFn;
    error: AlertFn;
    warning: AlertFn;
    info: AlertFn;
}

const AlertContext = createContext<AlertContextValue>({
    success: () => { },
    error: () => { },
    warning: () => { },
    info: () => { },
});

export const useAlert = () => useContext(AlertContext);

/** Configuration visuelle par sévérité. */
const SEVERITY_CONFIG: Record<Severity, {
    icon: React.ReactNode;
    gradient: string;
    accent: string;
}> = {
    success: {
        icon: <CheckCircle2 size={22} />,
        gradient: 'linear-gradient(135deg, #16A34A 0%, #22C55E 100%)',
        accent: '#16A34A',
    },
    error: {
        icon: <XCircle size={22} />,
        gradient: 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
        accent: '#DC2626',
    },
    warning: {
        icon: <AlertTriangle size={22} />,
        gradient: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
        accent: '#F59E0B',
    },
    info: {
        icon: <Info size={22} />,
        gradient: 'linear-gradient(135deg, #4338CA 0%, #6366F1 100%)',
        accent: '#4338CA',
    },
};

const AUTO_DISMISS_MS = 5000;

/**
 * Fournisseur d'alertes global.
 * Affiche des notifications empilées en bas à droite, remplaçant sweetalert2.
 *
 * Usage :
 *   const alert = useAlert();
 *   alert.success("Enregistré avec succès");
 *   alert.error("Erreur", "Détails de l'erreur");
 *   alert.warning("Attention", "Champ requis manquant");
 */
export const AlertProvider = ({ children }: { children: ReactNode }) => {
    const [alerts, setAlerts] = useState<AlertItem[]>([]);

    const handleClose = useCallback((id: number) => {
        setAlerts((prev) => prev.map((a) => a.id === id ? { ...a, closing: true } : a));
        setTimeout(() => {
            setAlerts((prev) => prev.filter((a) => a.id !== id));
        }, 280);
    }, []);

    const push = useCallback((severity: Severity, title: string, text?: string) => {
        const toText = (v: any): string | undefined => {
            if (v == null) return undefined;
            if (typeof v === 'string') return v;
            if (v instanceof Error) return v.message;
            if (typeof v.message === 'string') return v.message;
            return String(v);
        };
        const id = Date.now() + Math.random();
        setAlerts((prev) => [...prev, { id, severity, title: toText(title) || '', text: toText(text) }]);
        setTimeout(() => handleClose(id), AUTO_DISMISS_MS);
    }, [handleClose]);

    // Relais des erreurs globales (promesses rejetées, erreurs hors render)
    // émises par envGuard.ts -> toast d'erreur au lieu d'un échec silencieux.
    useEffect(() => {
        const onGlobalError = (e: Event) => {
            const message = (e as CustomEvent).detail;
            push('error', 'Erreur inattendue', typeof message === 'string' ? message : String(message));
        };
        window.addEventListener('eb:global-error', onGlobalError);
        return () => window.removeEventListener('eb:global-error', onGlobalError);
    }, [push]);

    const value: AlertContextValue = {
        success: useCallback((t, x) => push('success', t, x), [push]),
        error: useCallback((t, x) => push('error', t, x), [push]),
        warning: useCallback((t, x) => push('warning', t, x), [push]),
        info: useCallback((t, x) => push('info', t, x), [push]),
    };

    return (
        <AlertContext.Provider value={value}>
            {children}
            <div className="fixed bottom-6 right-6 z-[2000] flex flex-col gap-3 items-end pointer-events-none [&>*]:pointer-events-auto">
                {alerts.map((a) => (
                    <AlertCard key={a.id} alert={a} onClose={() => handleClose(a.id)} />
                ))}
            </div>
        </AlertContext.Provider>
    );
};

/** Carte d'alerte individuelle avec animations et barre de progression. */
const AlertCard = ({ alert, onClose }: { alert: AlertItem; onClose: () => void }) => {
    const config = SEVERITY_CONFIG[alert.severity];
    const [progress, setProgress] = useState(100);
    const startTimeRef = useRef(Date.now());
    const rafRef = useRef<number>();

    useEffect(() => {
        const tick = () => {
            const elapsed = Date.now() - startTimeRef.current;
            const remaining = Math.max(0, 100 - (elapsed / AUTO_DISMISS_MS) * 100);
            setProgress(remaining);
            if (remaining > 0 && !alert.closing) {
                rafRef.current = requestAnimationFrame(tick);
            }
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [alert.closing]);

    return (
        <div
            className="relative min-w-[340px] max-w-[460px] rounded-[14px] overflow-hidden border border-[var(--eb-border)] bg-[var(--eb-surface)]/98 backdrop-blur-md"
            style={{
                animation: alert.closing
                    ? 'ebAlertOut 0.28s cubic-bezier(0.4, 0, 0.6, 1) forwards'
                    : 'ebAlertIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
        >
            {/* Bande colorée gauche */}
            <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: config.gradient }} />

            {/* Contenu */}
            <div className="flex items-start gap-3 pt-4 pr-4 pb-3.5 pl-5">
                <div
                    className="min-w-[40px] w-10 h-10 rounded-full text-white flex items-center justify-center flex-shrink-0"
                    style={{ background: config.gradient }}
                >
                    {config.icon}
                </div>

                <div className="flex-1 min-w-0 pt-0.5">
                    <div className="font-bold text-[14.5px] text-[var(--eb-text)] leading-tight tracking-tight">
                        {alert.title}
                    </div>
                    {alert.text && (
                        <div className="text-[13px] text-[var(--eb-text-muted)] mt-1 leading-snug">
                            {alert.text}
                        </div>
                    )}
                </div>

                <button
                    onClick={onClose}
                    className="p-1 -mt-0.5 text-[var(--eb-text-muted)] hover:bg-[var(--eb-bg)] hover:text-[var(--eb-text)] rounded-md transition-colors"
                >
                    <X size={18} />
                </button>
            </div>

            {/* Barre de progression */}
            <div className="h-[3px] bg-[var(--eb-border)]/50 relative overflow-hidden">
                <div
                    className="absolute left-0 top-0 bottom-0"
                    style={{ width: `${progress}%`, background: config.gradient }}
                />
            </div>
        </div>
    );
};

export default AlertProvider;
